import React from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';
import SectionHeader from '../ui/SectionHeader';

interface ProblemCard {
  icon: string;
  color: 'teal' | 'amber' | 'emerald' | 'navy';
  headline: string;
  body: string;
}

const problems: ProblemCard[] = [
  {
    icon: '🌿',
    color: 'teal',
    headline: '"My CSRD filing needs Scope 3 supplier data. My suppliers can\'t produce it."',
    body: 'You are responsible for a verified corporate carbon footprint — but the most significant emissions are in your supply chain. Getting accurate, product-level carbon data from tier-1 and tier-2 suppliers, many outside the EU, is the hardest unsolved problem in corporate sustainability.',
  },
  {
    icon: '⚖️',
    color: 'amber',
    headline: '"CBAM is a financial liability I can\'t yet quantify."',
    body: 'If you import steel, aluminium, cement, fertilisers or chemicals from outside the EU, you owe carbon tax from January 2026. Embedded carbon per shipment must be calculated, supplier certificates collected, and residual liability paid or offset. Most companies have no system for any of this.',
  },
  {
    icon: '🔧',
    color: 'navy',
    headline: '"I have five tools and none of them talk to each other."',
    body: 'One platform for supplier surveys. Another for carbon calculations. A spreadsheet for CBAM. A separate system for procurement certificates. Every reporting period, someone reconciles data between systems manually — one number changes and the whole chain needs updating.',
  },
  {
    icon: '📋',
    color: 'emerald',
    headline: '"My auditors are asking questions I can\'t answer with confidence."',
    body: 'Carbon disclosures are moving from voluntary to mandatory and from qualitative to quantified. Auditors, investors and your board want verified numbers with a clear data trail — not estimates built on assumptions. If your carbon data isn\'t sourced, validated and auditable, your next filing is a risk event.',
  },
];

export default function ProblemCards(): React.ReactElement {
  const ref = useScrollReveal();

  return (
    <section
      className="problems section-pad"
      id="problems"
      ref={ref as React.RefObject<HTMLElement>}
    >
      <div className="container">
        <SectionHeader
          eyebrow="The problem"
          title="ESG compliance has a data problem. We built the fix."
          description="The landscape has changed. CSRD, EU Taxonomy, CBAM, and BRSR are live obligations — and none of them can be met by a generic data collection approach."
        />
        <div className="problems-grid">
          {problems.map((p, i) => (
            <div
              key={i}
              className={`problem-card problem-card--${p.color} reveal reveal-delay-${(i % 4) + 1}`}
            >
              <span className="problem-card-icon">{p.icon}</span>
              <p className="problem-card-headline">{p.headline}</p>
              <p className="problem-card-body">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
